import { Alert } from './util/alert';
import { debug } from '@gebruederheitz/debuggable';
import mitt from 'mitt';
import { Route } from './util/route';
import { toasts } from './util/toast';
import { URLS } from './config';
import { localizeOrNull } from './util/i18n/localize';
import { HttpVerb } from './util/http-verb.enum';
class MaintenanceModeException extends Error {
}
export var AuthenticatedHttpConnectorEvents;
(function (AuthenticatedHttpConnectorEvents) {
    AuthenticatedHttpConnectorEvents["CODED_ERROR"] = "coded-error";
})(AuthenticatedHttpConnectorEvents || (AuthenticatedHttpConnectorEvents = {}));
export var SdkEvents;
(function (SdkEvents) {
    SdkEvents["READY"] = "ready";
})(SdkEvents || (SdkEvents = {}));
/**
 * Stateless-ish HTTP client that performs token-authenticated requests against
 * the game API, transparently handling maintenance mode, coded errors and
 * expired tokens. Consumed by {@link SdkCore} via composition rather than
 * inheritance.
 */
export class HttpService {
    url;
    getToken;
    onTokenExpired;
    credentials;
    eventProxy;
    maintenanceMode = false;
    maintenanceAlertOpen = false;
    constructor({ url = URLS.LOCAL, getToken, onTokenExpired, credentials, emitter, } = {}) {
        this.url = url;
        this.getToken = getToken ?? (() => Promise.resolve(null));
        this.onTokenExpired = onTokenExpired ?? (() => { });
        this.credentials = credentials;
        this.eventProxy = emitter ?? mitt();
    }
    getBaseUrl() {
        return this.url;
    }
    get on() {
        return this.eventProxy.on.bind(this.eventProxy);
    }
    get off() {
        return this.eventProxy.off.bind(this.eventProxy);
    }
    requestRoute(route, data, ...pathArgs) {
        return this.requestRouteWithOptions(route, {}, data, ...pathArgs);
    }
    /**
     * Like {@link requestRoute}, but with per-request {@link RequestOptions}.
     * The options come second because the path arguments are variadic.
     */
    requestRouteWithOptions(route, options, data, ...pathArgs) {
        const path = route.path(...pathArgs);
        let callback;
        switch (route.method) {
            case HttpVerb.POST_JSON:
                callback = () => this.post(path, data, options);
                break;
            case HttpVerb.POST_FORM:
                callback = () => this.postForm(path, data, options);
                break;
            case HttpVerb.PATCH:
                callback = () => this.patch(path, data, options);
                break;
            case HttpVerb.DELETE:
                callback = () => this.delete(path, data, options);
                break;
            case HttpVerb.PUT:
                callback = () => this.put(path, data, options);
                break;
            case HttpVerb.GET:
            default:
                callback = () => this.get(path, data, options);
        }
        return this.withRecursiveMaintenanceModeHandler(callback);
    }
    /**
     * Perform a standard GET request with token authentication.
     */
    get(path, params, options = {}) {
        let query = '';
        if (params) {
            query = new URLSearchParams(params).toString();
            query = '?' + query;
        }
        return this.makeRequest(path + query, 'GET', { ...options });
    }
    /**
     * Perform an authenticated POST request to the API server.
     */
    post(path, data = {}, options = {}) {
        return this.makeRequest(path, 'POST', {
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(data),
            ...options,
        });
    }
    /**
     * Perform an authenticated DELETE request to the API server.
     */
    delete(path, data = {}, options = {}) {
        return this.makeRequest(path, 'DELETE', {
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(data),
            ...options,
        });
    }
    /**
     * Perform an authenticated PATCH request to the API server.
     */
    patch(path, data = {}, options = {}) {
        return this.makeRequest(path, 'PATCH', {
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(data),
            ...options,
        });
    }
    /**
     * Perform an authenticated PUT request to the API server.
     */
    put(path, data = {}, options = {}) {
        return this.makeRequest(path, 'PUT', {
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(data),
            ...options,
        });
    }
    /**
     * Perform an authenticated POST request to the API server using form-data.
     */
    postForm(path, data, options = {}) {
        return this.makeRequest(path, 'POST', {
            headers: {
            // no content-type, so the browser can set multipart/form-data _with a form boundary_!
            },
            body: data,
            ...options,
        });
    }
    async makeRequest(path, method = 'GET', { headers = {}, body = null, onUploadProgress, onNetworkError, onCodedError, } = {}) {
        try {
            const token = await this.getToken();
            const baseHeaders = {
                accept: 'application/json',
                ...headers,
            };
            if (token) {
                baseHeaders.authorization = `Bearer ${token}`;
            }
            const options = { method, headers: baseHeaders };
            if (this.credentials) {
                options.credentials = this.credentials;
            }
            if (body !== null) {
                options.body = body;
            }
            let r;
            if (onUploadProgress && body instanceof FormData) {
                r = await this.xhrUpload(`${this.url}/api/${path}`, method, baseHeaders, body, onUploadProgress);
            }
            else {
                r = await fetch(`${this.url}/api/${path}`, options);
            }
            if (r.status === 503) {
                throw new MaintenanceModeException();
            }
            if (r.status === 404) {
                return null;
            }
            const content = await this.safeJson(r);
            const isErrorObject = (c) => !!(c && typeof c === 'object' && 'code' in c);
            const hasMessageProp = (c) => !!(c && typeof c === 'object' && 'message' in c);
            if (r.status === 401 && isErrorObject(content)) {
                if (content.code === 'token-expired') {
                    await this.onTokenExpired();
                    // We leave it to the caller to throw an exception or show a
                    // toast if they so desire.
                    return null;
                }
            }
            if (r.status > 299) {
                if (isErrorObject(content)) {
                    if (onCodedError) {
                        onCodedError(content.code);
                    }
                    else {
                        this.eventProxy.emit(AuthenticatedHttpConnectorEvents.CODED_ERROR, content.code);
                    }
                }
                else {
                    toasts.make((hasMessageProp(content) && content.message) ||
                        'Unknown error', 'error');
                }
                return null;
            }
            // A `code` property only marks a coded error on non-2xx responses
            // (handled above) — successful payloads may legitimately carry
            // one, e.g. a game's join code.
            return content;
        }
        catch (e) {
            if (e instanceof MaintenanceModeException) {
                // rethrow this exception, as it needs to end up in the MM handler
                throw e;
            }
            console.warn('Failed to establish API connection', e);
            onNetworkError?.();
            return null;
        }
    }
    /**
     * Perform an upload via XMLHttpRequest so that upload progress can be
     * reported — fetch() has no upload-progress support. Resolves with a
     * fetch-compatible {@link TransportResponse} so the regular response
     * handling in {@link makeRequest} applies unchanged.
     */
    xhrUpload(url, method, headers, body, onUploadProgress) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open(method, url);
            // The headers deliberately exclude content-type for form posts, so
            // the browser sets multipart/form-data with a form boundary.
            Object.entries(headers).forEach(([name, value]) => {
                xhr.setRequestHeader(name, value);
            });
            if (this.credentials === 'include') {
                xhr.withCredentials = true;
            }
            // No xhr.timeout on purpose: large uploads on mobile networks can
            // take minutes, and the fetch path has no timeout either.
            xhr.upload.onprogress = (e) => {
                onUploadProgress(e.loaded, e.lengthComputable ? e.total : null);
            };
            xhr.onload = () => {
                // Status 0 means the request never reached the server (blocked,
                // CORS, connection reset). Normally onerror fires for these,
                // but if one ever lands here it must not slip through the
                // status checks as a silent success.
                if (xhr.status === 0) {
                    reject(new Error('XHR network error (status 0)'));
                    return;
                }
                resolve({
                    status: xhr.status,
                    // A synchronous parse error still lands in safeJson()'s
                    // catch and yields null, matching the fetch path.
                    json: () => Promise.resolve(JSON.parse(xhr.responseText)),
                });
            };
            xhr.onerror = () => reject(new Error('XHR network error'));
            xhr.onabort = () => reject(new Error('XHR aborted'));
            xhr.ontimeout = () => reject(new Error('XHR timeout'));
            xhr.send(body);
        });
    }
    async safeJson(response) {
        try {
            return await response.json();
        }
        catch {
            return null;
        }
    }
    async withRecursiveMaintenanceModeHandler(callback) {
        let caught = false;
        try {
            return await callback();
        }
        catch (e) {
            if (e instanceof MaintenanceModeException) {
                caught = true;
                if (!(this.maintenanceMode && this.maintenanceAlertOpen)) {
                    this.enableMaintenanceMode();
                }
                await debug.timeout(8000);
                return this.withRecursiveMaintenanceModeHandler(callback);
            }
            else {
                // rethrow any non-MM exceptions, we shouldn't even be receiving any
                throw e;
            }
        }
        finally {
            if (this.maintenanceMode && !caught) {
                // MM is active, but the last request went through: reset
                this.disableMaintenanceMode();
            }
        }
    }
    enableMaintenanceMode() {
        this.maintenanceMode = true;
        this.maintenanceAlertOpen = true;
        const alertOptions = {
            title: localizeOrNull('maintenance.title') ?? '',
            text: localizeOrNull('maintenance.body') ?? '',
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            showConfirmButton: false,
            icon: undefined,
            imageUrl: this.url + '/img/maintenance.jpg',
        };
        // Fire-and-forget: the alert resolves only when we programmatically
        // close it in disableMaintenanceMode(), so awaiting it here would stall
        // the retry loop indefinitely.
        void Alert.fire(alertOptions);
    }
    disableMaintenanceMode() {
        Alert.close();
        this.maintenanceAlertOpen = false;
        this.maintenanceMode = false;
    }
}
//# sourceMappingURL=authenticated-http-connector.js.map